import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bounce, toast } from 'react-toastify'

import axios, { AxiosResponse } from 'axios'

import { CornerRightUp } from '@/src/assets/icons'

import { useRoomContext } from '@/src/context'
import { Toast } from '@/src/components/Toast/Toast'

import {
  IChatForm,
  IMessage,
  IMessageBodyRequest,
  IMessagesNoGetResponse,
} from '@/src/types/chat.type'
import { BASE_URL_MEET } from '@/src/constants/env'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import { handlePublishData } from '@/src/helpers/handlePublishData'
import { useGroups } from '@/src/hooks/participant/useGroups'

export const ChatForm = ({ isPiP, getMessages }: IChatForm) => {
  const { t } = useTranslation()
  const room = useRoomContext()
  const dispatch = useAppDispatch()

  const {
    primary_color,
    text_primary_color,
    border_radius: borderRadius,
  } = useAppSelector(selectBranding)

  const [message, setMessage] = useState<string>('')

  const { getCurrentUserGroupId } = useGroups()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event?.target?.value)
  }

  const handleSubmit = async (event) => {
    event?.preventDefault()
    event.stopPropagation()

    if (message?.trim()?.length > 0) {
      const created_at = new Date()?.getTime()

      const newMessage: IMessage = {
        id: +Math.random()?.toString()?.split('.')?.at(1),

        author: {
          id: room?.localParticipant?.identity?.trim(),
          name: room?.localParticipant?.name?.trim(),
        },

        message,

        isViewed: false,

        created_at,
        updated_at: created_at,
        groupId: getCurrentUserGroupId,
      }

      const roomId: string | null = await room?.getSid()?.catch((err) => {
        console.error('Error with getting identity of room: \n', err)
        return null
      })

      if (!roomId || typeof roomId !== 'string') return

      try {
        await axios
          .post<IMessagesNoGetResponse, AxiosResponse<IMessagesNoGetResponse>, IMessageBodyRequest>(
            `${BASE_URL_MEET}/api/chat/messages`,
            {
              roomName: room?.name,
              roomId,
              message: newMessage,
            },
          )
          ?.catch((e) => {
            toast.error(
              (content) => (
                <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
              ),
              {
                ariaLabel: t('toast.error'),
                data: t('chat.errorWithSendMessage'),
                role: 'alert',
                position: 'bottom-right',
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'dark',
                transition: Bounce,
              },
            )
            console.error('Error with sending message to chat: \n', e)
          })
          ?.then(() => {
            setMessage('')

            handlePublishData(
              room,
              { updateMessages: true },
              {
                reliable: true,
                topic: 'chat-topic',
              },
            )
            getMessages()
          })
      } catch (e) {
        console.error('Error with parsed metadata before send message: \n', e)
      }
    }
  }

  return (
    <form className="lk-chat-form" onSubmit={handleSubmit}>
      <input
        value={message}
        onChange={handleChange}
        type="text"
        placeholder={t('sendPlaceholder')}
        style={{ borderRadius }}
        className="lk-form-control lk-chat-form-input"
      />

      <button
        type="submit"
        style={
          isPiP && {
            backgroundColor: primary_color,
            color: text_primary_color,
          }
        }
        className="lk-button lk-chat-form-button"
      >
        {isPiP ? <CornerRightUp /> : t('send')}
      </button>
    </form>
  )
}
